<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      :iconHeader="'add'"
      :items="displayedAlerts"
      :headers="tableHeaders"
      :disablePagination="true"
      :title="$t('sitePage.alerts')"
      :pagination="pagination"
      isSearchable
      hideDefaultFooter
      @iconAction="onClickAdd"
    >
      <template #item.alertedBy="{ item }">
        <AlertedByGrid
          :user="item.beAlertedBy"
          :alertId="item.id"
          :isLoading="loading"
          @onSelectionChange="onAlertByGridChange"
        />
      </template>
      <template
        v-slot:item.alterAction="{ item }"
      >
        <div :class="['action-buttons',{'disable-action': itemActions(item).length===0}]">
          <VTableAction
            :itemActions="itemActions(item)"
            :isCustomIcon="true"
            @iconAction="(action)=>onClickAction(item, action)"
          />
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import roles from '@/constants/roles';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import AlertedByGrid from '@/components/sites/AlertedByGrid';
import AlertForm from '@/components/forms/AlertForm';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableAction from '@/components/shared/widgets/VTableAction';


export default {
    components: {
        VTableRemote,
        AlertedByGrid,
        VTableAction,
    },
    data() {
        return {
            site: { ...this.$store.getters.site },
            loading: false,
            isInitializing: false,
            displayedAlerts: [],
            pagination: { },
            queryParams: {
                perpage: 25,
                page: 1,
            },
            verticalActionDots: false,
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'hasRole',
            'currentMember',
        ]),
        tableHeaders() {
            const headerArr = [
                { label: 'name', ref: 'name' },
                { label: 'type', ref: 'activity.label' },
                { label: 'people', ref: 'alertedPeopleCount' },
                { label: 'message', ref: 'message' },
                { label: 'alertedBy', ref: 'alertedBy' },
            ].map(head => ({
                sortable: true,
                text: this.$t(`alerts.labels.${head.label}`),
                value: head.ref,
            }));
            headerArr.push({
                sortable: false,
                text: this.$t('controls.actions'),
                value: 'alterAction',
            });
            return headerArr;
        },
    },

    created() {
        this.isInitializing = true;
        this.getSiteAlerts();
        this.isInitializing = false;
    },
    methods: {
        async getSiteAlerts(clearAll = true) {
            this.loading = true;
            if (this.site.id) {
                const { data } = await this.$rq.getSiteAlerts({ siteId: this.site.id, queryParams: this.queryParams });
                this.displayedAlerts = clearAll ? data.alerts : [...this.displayedAlerts, ...data.alerts];
                this.pagination = data.pagination;
            }
            this.loading = false;
        },
        itemActions(itemRef) {
            let arr = [];
            if (this.hasRole(roles.ADMINISTRATOR.value) || this.hasRole(roles.MANAGER.value)
                || this.hasRole(roles.LABORATORY_MANAGER.value)
                || (this.currentMember.id === itemRef.createdBy?.memberId)) {
                arr = [{
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                }, {
                    name: this.$t('sitePage.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                }];
                this.verticalActionDots = true;
            } else {
                arr = [{
                    name: this.$t('forms.btn.view'),
                    icon: 'mdi-eye',
                    mode: 'view',
                }];
                this.verticalActionDots = true;
            }
            return arr;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('alerts.labels.addAlert'),
                component: Vue.component('AlertForm', AlertForm),
                payload: {
                    site: this.site,
                },
                isForm: false,
                onConfirm: requestPayload => this.addAlert(requestPayload),
            });
        },
        generateAlertPayload(requestPayload) {
            if (!requestPayload) return null;
            let productInventories;
            let analysisTypes;
            const { initValue: peopleInit, ...alPeople } = requestPayload.alPeople;
            const { initValue: producInit, ...alProducts } = requestPayload.alProducts;
            const alertType = requestPayload.alType.key || requestPayload.alType;
            if (alertType === 'measuresAndSamplesCollect') {
                analysisTypes = requestPayload.alNorms.map(norm => ({
                    analysisType: norm.id,
                    parentNetwork: norm.parentNetwork,
                    component: norm.component,
                    norms: {
                        veryLow: !!norm.norms?.veryLow,
                        low: !!norm?.norms?.low,
                        minMax: !!norm?.norms?.minMax,
                        high: !!norm?.norms?.high,
                        veryHigh: !!norm?.norms?.veryHigh,
                    },
                }));
            } else if (!alProducts.initValue) {
                productInventories = Object.values(alProducts).map(inventory => ({
                    productInventory: inventory.id,
                    alertQuantityMin: inventory.alertQuantityMin,
                }));
            }

            const payload = {
                name: requestPayload.alertName,
                type: alertType,
                productInventories,
                analysisTypes,
                alertedPeople: Object.entries(alPeople).map(([memberId, vals]) => ({
                    member: vals.isMember ? memberId : undefined,
                    contact: vals.isContact ? memberId : undefined,
                    sms: vals.sms ? vals.sms : false,
                    email: vals.email ? vals.email : false,
                    app: vals.app ? vals.app : false,
                })),
            };
            return payload;
        },
        async addAlert(requestPayload) {
            const payload = this.generateAlertPayload(requestPayload);
            await this.$rq.addSiteAlerts({ siteId: this.site.id, payload });
            this.fetchQuery();
        },
        async updateAlert(requestPayload, alert) {
            const payload = this.generateAlertPayload(requestPayload);
            // TODO isForm=true for modal doesn't work and we can send update request with invalid payload need to be fixed
            if (payload) {
                await this.$rq.updateSiteAlert({ siteId: this.site.id, alertId: alert.id, payload });
            }
            this.fetchQuery();
        },
        async deleteAlert(id) {
            await this.$rq.deleteSiteAlert({ siteId: this.site.id, alertId: id });
            this.fetchQuery();
        },
        async onClickAction(alert, action) {
            if (alert === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'edit') {
                const { data } = await this.$rq.getSiteAlertById({ siteId: this.site.id, alertId: alert.id });
                this.$root.$emit('showModal', {
                    title: this.$t('alerts.labels.editAlert'),
                    component: Vue.component('AlertForm', AlertForm),
                    payload: {
                        site: this.site,
                        isEdition: true,
                        alertData: data,
                    },
                    isForm: true,
                    onConfirm: requestPayload => this.updateAlert(requestPayload, alert),
                });
            } else if (action.mode === 'view') {
                const { data } = await this.$rq.getSiteAlertById({ siteId: this.site.id, alertId: alert.id });
                this.$root.$emit('showModal', {
                    title: this.$t('alerts.labels.editAlert'),
                    component: Vue.component('AlertForm', AlertForm),
                    payload: {
                        site: this.site,
                        isEdition: true,
                        alertData: data,
                        readOnly: true,
                    },
                    isForm: true,
                    showConfirmButton: false,
                });
            } else {
                this.$root.$emit('showModal', {
                    title: this.$t('alerts.labels.confirmDelete'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('alerts.confirmRemove') },
                    onConfirm: () => this.deleteAlert(alert.id),
                    isForm: false,
                });
            }
        },
        async onAlertByGridChange(updateDt) {
            this.loading = true;
            const { updatedData: payload, alertId } = updateDt;
            await this.$rq.updateSiteAlertSubscription({ siteId: this.site.id, alertId, payload });
            await this.getSiteAlerts();
            this.loading = false;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getSiteAlerts(false);
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        fetchQuery() {
            this.queryParams = { perpage: 25, page: 1 };
            this.pagination = {};
            this.displayedAlerts = [];
            this.getSiteAlerts();
        },
    },
};
</script>
<style>
.text-start .disable-action button{
    background-color: #c7c7c7 !important;
    pointer-events: none;
    cursor: default;
}
.disable-action button .v-btn__content{
    color: #A4A2A2;
}
</style>
